import { useChat } from "@ai-sdk/react";
import { SendIcon, XIcon, Trash2Icon } from "lucide-react";
import Markdown from "react-markdown";
import { Button } from "~/components/ui/button";
import { Textarea } from "~/components/ui/textarea";
import { cn } from "~/lib/utils";
import { atom, useAtom } from "jotai";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "~/components/ui/tooltip";
import { m } from "~/i18n/messages";
import { useEffect, useRef } from "react";

export type ChatWidgetContext = {
	title: string;
	slug: string;
	// biome-ignore lint/suspicious/noExplicitAny: it's ok, we only use it for the LLM, we don't need to care about its content
	contextData?: Record<string, any>;
};

type ChatWidgetProps = {
	placeholder?: string;
	instructionText?: string;
	isOpen: boolean;
	onClose: () => void;
	contextText?: string;
};

export const currentPageContextAtom = atom<ChatWidgetContext>({
	title: m["chat.title"](),
	slug: "/app/overview",
	contextData: {},
});

export function ChatWidget({
	placeholder = m["chat.input_placeholder"](),
	instructionText = m["chat.instruction_text"](),
	onClose,
}: ChatWidgetProps) {
	const [pageContext] = useAtom(currentPageContextAtom);
	const messagesRef = useRef<HTMLDivElement>(null);

	const {
		messages,
		handleInputChange,
		handleSubmit,
		status,
		input,
		setMessages,
	} = useChat({
		api: "/api/chat/ephemeral",
		id: `ephemeral-chat:${pageContext?.slug}`,
		experimental_prepareRequestBody: (body) => {
			return {
				...body,
				context: pageContext?.contextData,
			};
		},
	});

	function onSubmit(e: React.FormEvent) {
		e.preventDefault();
		if (input.trim()) {
			handleSubmit(e);
		}
	}

	function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			handleSubmit(e);
		}
	}

	// biome-ignore lint/correctness/useExhaustiveDependencies: it runs everytime messages changed
	useEffect(() => {
		// automatically scroll to bottom when new messages are added
		messagesRef.current?.scrollTo(0, messagesRef.current.scrollHeight);
	}, [messages, messagesRef]);

	return (
		<TooltipProvider>
			<div className="rounded-lg md:rounded-b-none bg-white flex flex-col w-full h-full max-h-[100vh] md:max-h-[80vh] drop-shadow-xl border border-b-0 md:border-b-0 border-pink-200/50 md:max-h-[calc(100vh-4rem)]">
				<div className="flex justify-between items-center px-3 md:px-4 py-2 md:py-2 border-b border-pink-100">
					<div className="flex items-center gap-2">
						<h3 className="text-base md:text-lg font-semibold">{m["chat.title"]()}</h3>
					</div>
					<div className="flex items-center gap-1 md:gap-2">
						<Tooltip>
							<TooltipTrigger asChild>
								<Button
									variant="ghost"
									size="icon"
									className="h-8 w-8 md:h-9 md:w-9 touch-manipulation"
									onClick={() => setMessages([])}
								>
									<Trash2Icon className="h-3 w-3 md:h-4 md:w-4" />
									<span className="sr-only">
										{m["chat.clear_button_label"]()}
									</span>
								</Button>
							</TooltipTrigger>
							<TooltipContent>
								<p>{m["chat.clear_button_label"]()}</p>
							</TooltipContent>
						</Tooltip>
						<Button
							variant="ghost"
							size="icon"
							className="h-8 w-8 md:h-9 md:w-9 touch-manipulation"
							onClick={onClose}
						>
							<XIcon className="h-3 w-3 md:h-4 md:w-4" />
							<span className="sr-only">{m["chat.close_button_label"]()}</span>
						</Button>
					</div>
				</div>

				<div className="p-3 md:p-4 overflow-y-auto flex-1 min-h-0" ref={messagesRef}>
					{messages.length === 0 ? (
						<p className="text-pink-600/70 text-xs md:text-sm">{instructionText}</p>
					) : (
						<div className="space-y-2 md:space-y-3">
							{messages.map(
								(message) =>
									message.content !== "" && (
										<div
											key={message.id}
											className={cn(
												"rounded-lg px-2 md:px-3 py-1.5 md:py-2 text-xs md:text-sm [&_strong]:font-medium space-y-2 md:space-y-4 max-w-[85%]",
												{
													"bg-primary text-white rounded-tr-none ml-auto":
														message.role === "user",
													"bg-purple-100 rounded-tl-none text-neutral-700 [&_hr]:border-purple-200 mr-auto":
														message.role === "assistant",
												},
											)}
										>
											<Markdown>{message.content}</Markdown>
										</div>
									),
							)}
						</div>
					)}
					{status === "submitted" &&
						(messages.length === 0 ||
							messages[messages.length - 1].content === "") && (
							<div className="text-xs md:text-sm rounded-lg font-medium mt-2 bg-purple-50 px-2 md:px-3 py-1.5 md:py-2 max-w-fit animate-pulse">
								<div className="flex items-center gap-1">
									<div className="size-1.5 md:size-2 rounded-full bg-purple-500/50 animate-bounce" />
									<div className="size-1.5 md:size-2 rounded-full bg-purple-500/50 animate-bounce delay-100" />
									<div className="size-1.5 md:size-2 rounded-full bg-purple-500/50 animate-bounce delay-200" />
								</div>
							</div>
						)}
				</div>

				<form onSubmit={onSubmit} className="p-3 md:p-4 border-t border-pink-100">
					<div className="flex items-end gap-2">
						<Textarea
							className="flex-1 resize-none border-none focus-visible:ring-0 rounded-none p-0 min-h-[40px] md:min-h-[44px] text-sm md:text-base"
							placeholder={placeholder}
							value={input}
							onChange={handleInputChange}
							onKeyDown={handleKeyDown}
							disabled={status === "streaming" || status === "submitted"}
							rows={1}
						/>
						<Button
							type="submit"
							size="icon"
							className="h-8 w-8 md:h-9 md:w-9 touch-manipulation shrink-0"
							disabled={
								status === "streaming" ||
								status === "submitted" ||
								!input.trim()
							}
						>
							<SendIcon className="h-4 w-4 md:h-5 md:w-5" />
							<span className="sr-only">{m["chat.send_button_label"]()}</span>
						</Button>
					</div>
				</form>
			</div>
		</TooltipProvider>
	);
}
